import React, { useState, useEffect } from "react";
import { Register } from "../components/register"
import { UpdateUser} from "../components/updateUser"
import { ResetUserPassword} from "../components/resetUserPassword"
import SiteContact from "../components/siteContact"
import { BulkUploads } from "../components/bulkUploads"
import { EditCompanies } from "../components/editCompanies"
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { baseUrl } from '../shared';
import './App.css';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

function InspTypesDialog({ open, onClose }) {
  const [auditTypes, setAuditTypes] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [inspTypeMap, setInspTypeMap] = useState([]);
  useEffect(() => {
    if (!open) return;
    Promise.all([
      fetch(baseUrl + 'asset/descriptionAc', { headers: authHeaders() }).then(r => r.json()),
      fetch(baseUrl + 'workshop/settings/inspection-type-map', { headers: authHeaders() }).then(r => r.json()),
      fetch(baseUrl + 'inspection/audit-types', { headers: authHeaders() }).then(r => r.json()),
    ]).then(([at, im, aud]) => {
      setAssetTypes(Array.isArray(at) ? at : []);
      setInspTypeMap(Array.isArray(im) ? im : []);
      setAuditTypes(Array.isArray(aud) ? aud : []);
    });
  }, [open]);

  const isMapped = (item, auditType) => inspTypeMap.some(m => m.item === item && m.auditType === auditType);

  const toggle = async (item, auditType) => {
    const existing = inspTypeMap.find(m => m.item === item && m.auditType === auditType);
    if (existing) {
      await fetch(baseUrl + 'workshop/settings/inspection-type-map/' + existing.id, { method: 'DELETE', headers: authHeaders() });
      setInspTypeMap(prev => prev.filter(m => m.id !== existing.id));
    } else {
      await fetch(baseUrl + 'workshop/settings/inspection-type-map', {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ item, auditType }),
      });
      const rows = await fetch(baseUrl + 'workshop/settings/inspection-type-map', { headers: authHeaders() }).then(r => r.json());
      setInspTypeMap(Array.isArray(rows) ? rows : []);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Inspection Types
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowX: 'auto' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tick which inspection types apply to each asset type. If exactly one is ticked, it auto-selects when starting an inspection for that asset.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'var(--surface-2)' }}>
              <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>Asset Type</TableCell>
              {auditTypes.map(at => (
                <TableCell key={at.auditKey} align="center" sx={{ fontWeight: 600, fontSize: 11 }}>{at.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {assetTypes.map(at => (
              <TableRow key={at.value} hover>
                <TableCell sx={{ fontWeight: 600 }}>{at.label}</TableCell>
                {auditTypes.map(aud => (
                  <TableCell key={aud.auditKey} align="center" padding="checkbox">
                    <Checkbox size="small" checked={isMapped(at.value, aud.auditKey)} onChange={() => toggle(at.value, aud.auditKey)} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {assetTypes.length === 0 && (
              <TableRow><TableCell colSpan={(auditTypes.length || 1) + 1}><Typography color="text.secondary">No asset types configured.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}

function ChecklistsDialog({ open, onClose }) {
  const [auditTypes, setAuditTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState({});
  const [editingType, setEditingType] = useState({});
  const [adding, setAdding] = useState({});
  const [newTypeKey, setNewTypeKey] = useState('');
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [addingType, setAddingType] = useState(false);

  const reloadAll = async () => {
    const [aud, tpl] = await Promise.all([
      fetch(baseUrl + 'inspection/audit-types', { headers: authHeaders() }).then(r => r.json()),
      fetch(baseUrl + 'inspection/checklist-templates', { headers: authHeaders() }).then(r => r.json()),
    ]);
    setAuditTypes(Array.isArray(aud) ? aud : []);
    setTemplates(Array.isArray(tpl) ? tpl : []);
  };

  useEffect(() => { if (open) reloadAll(); }, [open]);

  const saveEdit = async (row) => {
    const newText = editing[row.id];
    if (!newText?.trim()) return;
    await fetch(baseUrl + 'inspection/checklist-templates/' + row.id, {
      method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ item: newText.trim(), sortOrder: row.sortOrder }),
    });
    setEditing(e => { const n = { ...e }; delete n[row.id]; return n; });
    await reloadAll();
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Remove this checklist item?')) return;
    await fetch(baseUrl + 'inspection/checklist-templates/' + id, { method: 'DELETE', headers: authHeaders() });
    await reloadAll();
  };

  const addItem = async (auditKey) => {
    const text = adding[auditKey]?.trim();
    if (!text) return;
    await fetch(baseUrl + 'inspection/checklist-templates', {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ auditType: auditKey, item: text }),
    });
    setAdding(a => { const n = { ...a }; delete n[auditKey]; return n; });
    await reloadAll();
  };

  const moveItem = async (items, idx, direction) => {
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const reordered = [...items];
    const tmp = reordered[idx];
    reordered[idx] = reordered[swapIdx];
    reordered[swapIdx] = tmp;
    await fetch(baseUrl + 'inspection/checklist-templates/reorder', {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(reordered.map((r, i) => ({ id: r.id, sortOrder: i + 1 }))),
    });
    await reloadAll();
  };

  const addAuditType = async () => {
    if (!newTypeKey.trim() || !newTypeLabel.trim()) return;
    const res = await fetch(baseUrl + 'inspection/audit-types', {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ auditKey: newTypeKey.trim(), label: newTypeLabel.trim() }),
    });
    if (!res.ok) {
      const err = await res.json();
      alert(err.message || 'Failed to add type');
      return;
    }
    setNewTypeKey('');
    setNewTypeLabel('');
    setAddingType(false);
    await reloadAll();
  };

  const saveTypeLabel = async (id) => {
    const newLabel = editingType[id];
    if (!newLabel?.trim()) return;
    const res = await fetch(baseUrl + 'inspection/audit-types/' + id, {
      method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: newLabel.trim() }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.message || 'Failed to rename type');
      return;
    }
    setEditingType(t => { const n = { ...t }; delete n[id]; return n; });
    await reloadAll();
  };

  const deleteAuditType = async (type) => {
    const items = templates.filter(t => t.auditType === type.auditKey);
    if (items.length > 0) {
      alert(`Remove all ${items.length} checklist item${items.length !== 1 ? 's' : ''} from "${type.label}" before deleting it.`);
      return;
    }
    if (!window.confirm(`Delete inspection type "${type.label}"? This cannot be undone.`)) return;
    await fetch(baseUrl + 'inspection/audit-types/' + type.id, { method: 'DELETE', headers: authHeaders() });
    await reloadAll();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Inspection Checklists
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Manage checklist items per inspection type. Changes apply to new inspections only.
          </Typography>
          <Button size="small" variant="outlined" onClick={() => setAddingType(v => !v)}>
            {addingType ? 'Cancel' : '+ New Type'}
          </Button>
        </Box>

        {addingType && (
          <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small" label="Key (e.g. ELECTRICAL)" sx={{ flex: 1 }}
              value={newTypeKey}
              onChange={e => setNewTypeKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
              placeholder="ELECTRICAL"
            />
            <TextField
              size="small" label="Display Label" sx={{ flex: 2 }}
              value={newTypeLabel}
              onChange={e => setNewTypeLabel(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addAuditType(); }}
            />
            <Button variant="contained" size="small" onClick={addAuditType} disabled={!newTypeKey.trim() || !newTypeLabel.trim()}>
              Add
            </Button>
          </Box>
        )}

        {auditTypes.map(({ id, auditKey, label }) => {
          const items = templates.filter(t => t.auditType === auditKey);
          return (
            <Accordion key={auditKey} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>
                  {editingType[id] !== undefined ? (
                    <>
                      <TextField
                        size="small" variant="standard" autoFocus
                        value={editingType[id]}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setEditingType(t => ({ ...t, [id]: e.target.value }))}
                        onKeyDown={e => {
                          e.stopPropagation();
                          if (e.key === 'Enter') saveTypeLabel(id);
                          if (e.key === 'Escape') setEditingType(t => { const n = { ...t }; delete n[id]; return n; });
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Button size="small" onClick={e => { e.stopPropagation(); saveTypeLabel(id); }}>Save</Button>
                      <Button size="small" onClick={e => { e.stopPropagation(); setEditingType(t => { const n = { ...t }; delete n[id]; return n; }); }}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {items.length} item{items.length !== 1 ? 's' : ''}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={e => { e.stopPropagation(); setEditingType(t => ({ ...t, [id]: label })); }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
                {editingType[id] === undefined && (
                  <IconButton
                    size="small" color="error"
                    onClick={e => { e.stopPropagation(); deleteAuditType({ id, auditKey, label }); }}
                    sx={{ mr: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {items.map((row, idx) => (
                  <Box
                    key={row.id}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 1.5, py: 0.5,
                      borderBottom: 1, borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 22, flexShrink: 0 }}>{idx + 1}.</Typography>
                    {editing[row.id] !== undefined ? (
                      <>
                        <TextField
                          size="small" variant="standard" autoFocus
                          value={editing[row.id]}
                          onChange={e => setEditing(ed => ({ ...ed, [row.id]: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveEdit(row);
                            if (e.key === 'Escape') setEditing(ed => { const n = { ...ed }; delete n[row.id]; return n; });
                          }}
                          sx={{ flex: 1 }}
                        />
                        <Button size="small" onClick={() => saveEdit(row)}>Save</Button>
                        <Button size="small" onClick={() => setEditing(ed => { const n = { ...ed }; delete n[row.id]; return n; })}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="body2" sx={{ flex: 1, cursor: 'pointer', py: 0.75 }}
                          onClick={() => setEditing(ed => ({ ...ed, [row.id]: row.item }))}
                        >
                          {row.item}
                        </Typography>
                        <IconButton size="small" onClick={() => moveItem(items, idx, -1)} disabled={idx === 0}>
                          <Typography sx={{ fontSize: 14, lineHeight: 1 }}>↑</Typography>
                        </IconButton>
                        <IconButton size="small" onClick={() => moveItem(items, idx, 1)} disabled={idx === items.length - 1}>
                          <Typography sx={{ fontSize: 14, lineHeight: 1 }}>↓</Typography>
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteItem(row.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                ))}
                <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', borderTop: items.length > 0 ? 1 : 0, borderColor: 'divider' }}>
                  <TextField
                    size="small" fullWidth placeholder="Add new checklist item..."
                    value={adding[auditKey] || ''}
                    onChange={e => setAdding(a => ({ ...a, [auditKey]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') addItem(auditKey); }}
                  />
                  <Button variant="outlined" size="small" onClick={() => addItem(auditKey)} disabled={!adding[auditKey]?.trim()}>Add</Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {auditTypes.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No inspection types yet. Click "+ New Type" to add one.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Login() {

  const [loginStatus, setLoginStatus] = useState(false);
  const [createNewUser, setCreateNewUser] = useState(false);
  const [updateUser, setUpdateUser] = useState(false);
  const [resetUserPass, setResettingUserPass] = useState(false);
  const [siteContactSetup, setSiteContactSetup] = useState(false);
  const [bulkUploadFunctions, setbulkUploadFunctions] = useState(false);
  const [editCompaniesOpen, setEditCompaniesOpen] = useState(false);
  const [inspTypesOpen, setInspTypesOpen] = useState(false);
  const [checklistsOpen, setChecklistsOpen] = useState(false);

  const createUser = () => {
    setCreateNewUser(true);
    console.log('create ',createNewUser)
  };  

  const updatingUser = () => {
    setUpdateUser(true);
    console.log('update ',updateUser)
  };  

  const resettingUserPass = () => {
    setResettingUserPass(true);
    console.log('resetUserPass ',resetUserPass)
  };  

  const siteContactSetupFunction = () => {
    setSiteContactSetup(true);
    console.log('siteContactSetup ',siteContactSetup)
  };  

  const bulkUploadFunction = () => {
    setbulkUploadFunctions(true);
    console.log('bulkUploadFunctions ',bulkUploadFunctions)
  };  

  useEffect(()=> {
    if (!localStorage.getItem('token')) {
      setLoginStatus(false);
    } else {
      setLoginStatus(true);
    }
  },[]); 

  
    // Register / UpdateUser / SiteContact / ResetUserPassword are overlay modals, so they
    // don't hide the landing menu. Only the full-page panels (bulk, companies) do.
    const onLanding = loginStatus && !bulkUploadFunctions && !editCompaniesOpen;
    const adminMenu = [
      { label: 'Create A New User', onClick: createUser, show: localStorage.getItem('canRegisterUser') === '1' },
      { label: 'Update User', onClick: updatingUser, show: localStorage.getItem('canUpdateUser') === '1' },
      { label: 'Reset User Password', onClick: resettingUserPass, show: localStorage.getItem('canUpdateUser') === '1' },
      { label: 'Set Site Contact', onClick: siteContactSetupFunction, show: localStorage.getItem('canBulkAdd') === '1' },
      { label: 'Bulk Upload Functions', onClick: bulkUploadFunction, show: localStorage.getItem('canBulkAdd') === '1' },
      { label: 'Edit Companies', onClick: () => setEditCompaniesOpen(true), show: localStorage.getItem('canRegisterUser') === '1' },
      { label: 'Inspection Types', onClick: () => setInspTypesOpen(true), show: localStorage.getItem('canRegisterUser') === '1' },
      { label: 'Inspection Checklists', onClick: () => setChecklistsOpen(true), show: localStorage.getItem('canRegisterUser') === '1' },
    ].filter(m => m.show);

    return (
      <div className="admin">
        { onLanding ? (
          <Box sx={{ maxWidth: 760, mx: 'auto', width: '100%' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>Admin</Typography>
            <Typography variant="body2" sx={{ color: 'var(--muted)', mb: 2 }}>
              Manage users, companies, and inspection configuration.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              {adminMenu.map(m => (
                <Button
                  key={m.label}
                  variant="outlined"
                  onClick={m.onClick}
                  sx={{
                    justifyContent: 'flex-start',
                    fontSize: 14,
                    py: 1.5,
                    px: 2,
                    background: 'var(--surface)',
                    borderColor: 'var(--border)',
                    boxShadow: 'var(--shadow-sm)',
                  }}
                >
                  {m.label}
                </Button>
              ))}
            </Box>
          </Box>
        ) : null}

    { localStorage.getItem('canRegisterUser') === '1' ? (
        <Dialog open={loginStatus && createNewUser} onClose={() => setCreateNewUser(false)} maxWidth="sm" fullWidth>
          <DialogContent sx={{ p: 0 }}>
            {createNewUser && <Register onClose={() => setCreateNewUser(false)} />}
          </DialogContent>
        </Dialog>
      ) : null}

    { localStorage.getItem('canUpdateUser') === '1' ? (
        <Dialog open={loginStatus && updateUser} onClose={() => setUpdateUser(false)} maxWidth="sm" fullWidth>
          <DialogContent sx={{ p: 0 }}>
            {updateUser && <UpdateUser onClose={() => setUpdateUser(false)} />}
          </DialogContent>
        </Dialog>
      ) : null}

      { localStorage.getItem('canUpdateUser') === '1' ? (
          <Dialog open={loginStatus && resetUserPass} onClose={() => setResettingUserPass(false)} maxWidth="sm" fullWidth>
            <DialogContent sx={{ p: 0 }}>
              {resetUserPass && <ResetUserPassword onClose={() => setResettingUserPass(false)} />}
            </DialogContent>
          </Dialog>
        ) : null}

        { (localStorage.getItem('canUpdateUser') === '1' || localStorage.getItem('canRegisterUser') === '1') ? (
            <Dialog open={loginStatus && siteContactSetup} onClose={() => setSiteContactSetup(false)} maxWidth="sm" fullWidth>
              <DialogContent sx={{ p: 0 }}>
                {siteContactSetup && <SiteContact onClose={() => setSiteContactSetup(false)} />}
              </DialogContent>
            </Dialog>
          ) : null}

        { loginStatus && bulkUploadFunctions && localStorage.getItem('canBulkAdd') === '1' ? (
            <BulkUploads onClose={() => setbulkUploadFunctions(false)} />
          ) : null}

        { loginStatus && editCompaniesOpen && localStorage.getItem('canRegisterUser') === '1' ? (
            <EditCompanies onClose={() => setEditCompaniesOpen(false)} />
          ) : null}

        <InspTypesDialog open={inspTypesOpen} onClose={() => setInspTypesOpen(false)} />
        <ChecklistsDialog open={checklistsOpen} onClose={() => setChecklistsOpen(false)} />

      </div>

    );
  }

export default Login